import { useEffect, useState } from 'react';
import { Star, ShieldCheck, MessageSquare, Send, Loader2 } from 'lucide-react';
import { reviewsAPI } from '../lib/api';
import { toast } from 'sonner';

/** Estrelas em tamanho variável */
export const StarRating = ({ value = 0, size = 16, className = '' }) => {
  const rounded = Math.round(value * 2) / 2; // meia estrela visual usa full/half
  return (
    <div className={`inline-flex items-center gap-0.5 ${className}`} data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= Math.floor(rounded);
        const half = !filled && i - 0.5 === rounded;
        return (
          <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
            <Star
              width={size}
              height={size}
              className="text-slate-300"
              strokeWidth={1.75}
            />
            {(filled || half) && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: half ? size / 2 : size }}
              >
                <Star
                  width={size}
                  height={size}
                  className="text-amber-400"
                  fill="currentColor"
                  strokeWidth={1.75}
                />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

const RatingSelector = ({ value, onChange, size = 30 }) => (
  <div className="inline-flex items-center gap-1" data-testid="rating-selector">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        className="transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
        aria-label={`${i} estrela${i > 1 ? 's' : ''}`}
        data-testid={`rating-star-${i}`}
      >
        <Star
          width={size}
          height={size}
          className={i <= value ? 'text-amber-400' : 'text-slate-300'}
          fill={i <= value ? 'currentColor' : 'none'}
          strokeWidth={1.75}
        />
      </button>
    ))}
  </div>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

export const ReviewsSection = ({ targetType, targetId }) => {
  const [summary, setSummary] = useState({ avg: 0, total: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 5, customer_name: '', comment: '', order_protocol: '' });
  const [loadError, setLoadError] = useState(false);

  const loadAll = async () => {
    if (!targetType || !targetId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        reviewsAPI.summary(targetType, targetId),
        reviewsAPI.list(targetType, targetId, { limit: 20 }),
      ]);
      setSummary({ avg: s.avg || 0, total: s.total || 0, distribution: s.distribution || {} });
      setItems(l.items || []);
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [targetType, targetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error('Informe seu nome'); return; }
    if (!form.order_protocol.trim()) { toast.error('Informe o protocolo do seu pedido — só quem comprou avalia'); return; }
    if (!form.comment.trim()) { toast.error('Escreva um comentário'); return; }
    if (form.rating < 1 || form.rating > 5) { toast.error('Selecione de 1 a 5 estrelas'); return; }
    setSubmitting(true);
    try {
      await reviewsAPI.create({
        target_type: targetType,
        target_id: targetId,
        rating: form.rating,
        comment: form.comment.trim(),
        customer_name: form.customer_name.trim(),
        order_protocol: form.order_protocol.trim() || undefined,
      });
      toast.success('Avaliação enviada! Ela aparecerá após aprovação do administrador.');
      setForm({ rating: 5, customer_name: '', comment: '', order_protocol: '' });
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  const maxDist = Math.max(1, ...Object.values(summary.distribution || {}));

  return (
    <section className="px-4 pb-6 pt-2" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-widest text-blue-700 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" /> Avaliações
        </h3>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          data-testid="toggle-review-form-btn"
        >
          {showForm ? 'Cancelar' : 'Escrever avaliação'}
        </button>
      </div>

      {/* Summary card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="reviews-summary-card">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black text-slate-900" data-testid="reviews-avg">
                {summary.total > 0 ? summary.avg.toFixed(1) : '—'}
              </span>
              <span className="text-sm text-slate-500">
                / 5 • {summary.total} {summary.total === 1 ? 'avaliação' : 'avaliações'}
              </span>
            </div>
            <StarRating value={summary.avg} size={18} className="mt-1" />
          </div>

          {/* Distribuição */}
          <div className="flex-1 max-w-sm space-y-1">
            {[5, 4, 3, 2, 1].map((star) => {
              const c = Number(summary.distribution?.[star] || 0);
              const pct = summary.total > 0 ? Math.round((c / summary.total) * 100) : 0;
              return (
                <div key={star} className="flex items-center gap-2 text-xs" data-testid={`dist-row-${star}`}>
                  <span className="w-4 text-slate-600 tabular-nums">{star}</span>
                  <Star className="w-3 h-3 text-amber-400" fill="currentColor" />
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full bg-amber-400"
                      style={{ width: `${(c / maxDist) * 100}%` }}
                    />
                  </div>
                  <span className="w-10 text-right text-slate-500 tabular-nums">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Formulário */}
      {showForm && (
        <form onSubmit={submit} className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/40 p-4 space-y-3" data-testid="review-form">
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Sua nota</label>
            <RatingSelector value={form.rating} onChange={(v) => setForm((s) => ({ ...s, rating: v }))} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Seu nome *</label>
              <input
                type="text"
                value={form.customer_name}
                onChange={(e) => setForm((s) => ({ ...s, customer_name: e.target.value }))}
                placeholder="Ex.: Maria Silva"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none text-sm"
                required
                data-testid="review-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Protocolo do pedido *</label>
              <input
                type="text"
                value={form.order_protocol}
                onChange={(e) => setForm((s) => ({ ...s, order_protocol: e.target.value.toUpperCase() }))}
                placeholder="DESTACK-2026-XXXXXX"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none text-sm font-mono"
                data-testid="review-protocol-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Comentário *</label>
            <textarea
              value={form.comment}
              onChange={(e) => setForm((s) => ({ ...s, comment: e.target.value }))}
              placeholder="Conte como foi a sua experiência (mín. 5 caracteres)"
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none text-sm resize-none"
              required
              data-testid="review-comment-input"
            />
          </div>
          <p className="text-[11px] text-slate-500">
            Só clientes com compra real avaliam — por isso pedimos o protocolo do pedido. Sua avaliação passará por moderação antes de ser publicada.
          </p>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60"
              data-testid="submit-review-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar avaliação
            </button>
          </div>
        </form>
      )}

      {/* Lista de avaliações */}
      <div className="mt-4 space-y-3" data-testid="reviews-list">
        {loadError ? (
          <div className="rounded-xl border border-rose-100 bg-rose-50 py-4 px-3 text-sm text-rose-700 flex items-center justify-between" data-testid="reviews-load-error">
            <span>Não conseguimos carregar as avaliações agora. Tente novamente.</span>
            <button
              type="button"
              onClick={loadAll}
              className="text-xs font-semibold px-3 py-1 rounded-full bg-rose-600 text-white hover:bg-rose-700"
            >
              Recarregar
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-sm text-slate-500" data-testid="reviews-empty">
            Seja o primeiro a avaliar. Sua opinião ajuda muita gente!
          </div>
        ) : (
          items.map((r) => (
            <article
              key={r.id}
              className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm"
              data-testid={`review-item-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 text-sm">{r.customer_name}</span>
                    {r.verified_purchase && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                        <ShieldCheck className="w-3 h-3" /> Compra verificada
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <StarRating value={r.rating} size={13} />
                    <span className="text-[11px] text-slate-400">{formatDate(r.created_at)}</span>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{r.comment}</p>
            </article>
          ))
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;
